import { useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Typography,
} from '@mui/material';
import api from 'api';
import { Permission } from 'api/models/response/permissions';
import AlertMessage, { AlertObj } from 'components/common/alert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { EditableUserSummary } from './EditUserProfileModal';

type PermissionsModalProps = {
    open: boolean;
    user: EditableUserSummary | null;
    onClose: () => void;
};

const PermissionsModal = ({ open, user, onClose }: PermissionsModalProps) => {
    const { t } = useTranslation();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
    const [assignedPermissions, setAssignedPermissions] = useState<Permission[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [updatingPermissionName, setUpdatingPermissionName] = useState<string | null>(null);
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });

    const userFullName = useMemo(() => {
        if (!user) return '';

        return `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email;
    }, [user]);

    useEffect(() => {
        if (!open || !user) {
            setAvailablePermissions([]);
            setAssignedPermissions([]);
            setUpdatingPermissionName(null);
            setIsLoading(false);
            return;
        }

        let isCancelled = false;

        const loadPermissions = async () => {
            setIsLoading(true);

            try {
                const [availableResponse, assignedResponse] = await Promise.all([
                    api.permissions.getAvailablePermissions(user.id),
                    api.permissions.getPermissions(user.id),
                ]);

                if (isCancelled) return;

                setAvailablePermissions(availableResponse?.data || []);
                setAssignedPermissions(assignedResponse?.data || []);
            } catch {
                if (isCancelled) return;

                setAvailablePermissions([]);
                setAssignedPermissions([]);
                setAlertMessage({
                    type: 'error',
                    value: t('somethingWentWrong', { lng: language }),
                });
                setShowingAlert(true);
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadPermissions();

        return () => {
            isCancelled = true;
        };
    }, [language, open, t, user]);

    const isPermissionChecked = (permissionName: string) =>
        assignedPermissions.some((permission) => permission?.name === permissionName);

    const handleTogglePermission = async (permission: Permission) => {
        if (!user) return;

        const isChecked = isPermissionChecked(permission.name);
        setUpdatingPermissionName(permission.name);

        try {
            if (isChecked) {
                await api.permissions.deletePermissions(user.id, permission.name);
                setAssignedPermissions((currentPermissions) =>
                    currentPermissions.filter(
                        (currentPermission) => currentPermission.name !== permission.name
                    )
                );
            } else {
                await api.permissions.addUserPermission(user.id, permission.name);
                setAssignedPermissions((currentPermissions) =>
                    currentPermissions.some(
                        (currentPermission) => currentPermission.name === permission.name
                    )
                        ? currentPermissions
                        : [...currentPermissions, permission]
                );
            }
        } catch {
            setAlertMessage({
                type: 'error',
                value: t('somethingWentWrong', { lng: language }),
            });
            setShowingAlert(true);
        } finally {
            setUpdatingPermissionName(null);
        }
    };

    if (!open || !user) return null;

    return (
        <>
            <AlertMessage
                type={alertMessage?.type}
                text={alertMessage?.value}
                open={isShowingAlert}
                onClose={() => setShowingAlert(false)}
            />
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xs"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            position: 'relative',
                            width: 'min(560px, calc(100% - 32px))',
                            maxWidth: '560px',
                            m: 2,
                            border: '1px solid rgba(131, 83, 226, 0.08)',
                            borderRadius: '28px',
                            backgroundColor: '#ffffff',
                            background:
                                'radial-gradient(circle at top right, rgba(131, 83, 226, 0.12), transparent 34%), linear-gradient(180deg, #ffffff 0%, #fcfbff 100%)',
                            boxShadow:
                                '0 18px 44px rgba(15, 23, 42, 0.08), 0 2px 10px rgba(15, 23, 42, 0.04)',
                            overflow: 'hidden',
                            '& .MuiDialogContent-root': {
                                backgroundColor: 'transparent',
                            },
                            '& .MuiDialogActions-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.72)',
                            },
                        },
                    },
                }}
            >
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 2,
                        width: 38,
                        height: 38,
                        border: '1px solid rgba(148, 163, 184, 0.24)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.88)',
                        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            background: '#ffffff',
                            borderColor: 'rgba(131, 83, 226, 0.32)',
                            boxShadow: '0 14px 28px rgba(131, 83, 226, 0.14)',
                        },
                    }}
                >
                    <Close sx={{ fontSize: 18, color: '#475569' }} />
                </IconButton>

                <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, pt: 3, pb: 0 }}>
                    <Box>
                        <Typography
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.25,
                                py: 0.75,
                                borderRadius: '999px',
                                background: 'rgba(131, 83, 226, 0.1)',
                                color: '#6d41d8',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                            }}
                        >
                            {t('permissions', { lng: language })}
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                mt: 1.25,
                                color: '#111827',
                                fontSize: '22px',
                                fontWeight: 700,
                                lineHeight: 1.2,
                            }}
                        >
                            {userFullName}
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.75,
                                color: '#64748b',
                                fontSize: '13px',
                                lineHeight: 1.5,
                            }}
                        >
                            Manage this user&apos;s access. Changes are saved as soon as a checkbox
                            is toggled.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mt: 2.5,
                            mb: 0.5,
                            display: 'grid',
                            gap: 1.25,
                            minHeight: 220,
                        }}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    minHeight: 220,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress size={28} sx={{ color: '#8353e2' }} />
                            </Box>
                        ) : availablePermissions.length > 0 ? (
                            availablePermissions.map((permission) => {
                                const checked = isPermissionChecked(permission.name);
                                const isUpdating = updatingPermissionName === permission.name;

                                return (
                                    <Box
                                        key={permission.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            p: 1.5,
                                            borderRadius: '18px',
                                            border: '1px solid rgba(148, 163, 184, 0.18)',
                                            background: 'rgba(255, 255, 255, 0.72)',
                                            backdropFilter: 'blur(12px)',
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    color: '#111827',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {permission.description || permission.name}
                                            </Typography>
                                            {permission.description &&
                                                permission.description !== permission.name && (
                                                    <Typography
                                                        sx={{
                                                            mt: 0.25,
                                                            color: '#64748b',
                                                            fontSize: '12px',
                                                            lineHeight: 1.4,
                                                        }}
                                                    >
                                                        {permission.name}
                                                    </Typography>
                                                )}
                                        </Box>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isUpdating ? (
                                                <CircularProgress
                                                    size={18}
                                                    sx={{ color: '#8353e2' }}
                                                />
                                            ) : (
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() =>
                                                        handleTogglePermission(permission)
                                                    }
                                                    sx={{
                                                        color: '#94a3b8',
                                                        '&.Mui-checked': {
                                                            color: '#8353e2',
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })
                        ) : (
                            <Box
                                sx={{
                                    minHeight: 220,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    color: '#64748b',
                                    fontSize: '14px',
                                }}
                            >
                                No permissions available for this user.
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2.25, sm: 3 },
                        py: 2.5,
                        mt: 1.5,
                        borderTop: '1px solid rgba(148, 163, 184, 0.18)',
                        background: 'rgba(255, 255, 255, 0.72)',
                        backdropFilter: 'blur(12px)',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Button
                        type="button"
                        onClick={onClose}
                        sx={{
                            height: 40,
                            px: 2.25,
                            borderRadius: '12px',
                            border: '1px solid rgba(148, 163, 184, 0.35)',
                            color: '#475569',
                            backgroundColor: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: 'rgba(131, 83, 226, 0.28)',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
                            },
                        }}
                    >
                        {t('close', { lng: language })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PermissionsModal;
